import type { ReactNode } from 'react';

// Plain layout parts shared by the landing pages: the converter pages (/png-to-svg and
// friends, through ConverterParts) and the free SVG tools (/svg-viewer and friends).
// Nothing here imports data, so a page can use them without the examples gallery.

export const linkClass = 'text-sky hover:text-sky-soft underline-offset-2 hover:underline transition-colors';

// ---------------------------------------------------------------------------
// Sections and prose
// ---------------------------------------------------------------------------

export function Section({
  id,
  heading,
  tone = 'white',
  children,
}: {
  id?: string;
  heading: ReactNode;
  tone?: 'white' | 'cream';
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className={`px-6 py-14 sm:py-20 border-t border-cream-200 ${tone === 'cream' ? 'bg-cream-100/50' : 'bg-white'}`}
    >
      <div className="max-w-[960px] mx-auto">
        <h2 className="text-[26px] sm:text-[32px] font-semibold tracking-[-0.02em] text-ink max-w-[700px] leading-[1.12]">
          {heading}
        </h2>
        <div className="mt-6">{children}</div>
      </div>
    </section>
  );
}

export function Prose({ children }: { children: ReactNode }) {
  return <div className="max-w-[700px] space-y-4 text-[15px] leading-relaxed text-ink-soft">{children}</div>;
}

export function Subhead({ children }: { children: ReactNode }) {
  return <h3 className="pt-3 text-[17px] font-semibold text-ink">{children}</h3>;
}

/** A short list of facts, each a label and a sentence or two. */
export function FactList({ items }: { items: { term: ReactNode; text: ReactNode }[] }) {
  return (
    <dl className="max-w-[760px] divide-y divide-cream-200 border-y border-cream-200">
      {items.map((item, i) => (
        <div key={i} className="grid gap-1 py-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-6">
          <dt className="text-[14px] font-semibold text-ink">{item.term}</dt>
          <dd className="text-[15px] leading-relaxed text-ink-soft">{item.text}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// FAQ (visible text only; pages that want FAQPage markup add it themselves)
// ---------------------------------------------------------------------------

export function Faq({ items }: { items: { q: string; a: ReactNode }[] }) {
  return (
    <dl className="max-w-[760px] divide-y divide-cream-200 border-y border-cream-200">
      {items.map((item) => (
        <div key={item.q} className="py-5">
          <dt className="text-[16px] font-semibold text-ink">{item.q}</dt>
          <dd className="mt-2 text-[15px] leading-relaxed text-ink-soft">{item.a}</dd>
        </div>
      ))}
    </dl>
  );
}
